import { useEffect, useMemo, useState } from "react";
import { useSnackbar } from "notistack";
import DataTable from "@accredo/fe-shared/components/DataTable.jsx";
import { fetchClaimCauseArten } from "@accredo/fe-shared/services/claimFormService.js";
import { useMultiTableData } from "./hooks/useMultiTableData.jsx";
import { useUser } from "@accredo/fe-shared/hooks/user.js";
import { getUserRowsPerPage } from "../../utils/userSettings.js";

export default function SchadenUrsachenTable() {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUser();
  const [arten, setArten] = useState([]);

  const tableKey = "schaden_ursachen";
  const storageKey = "v2_table_schaden_ursachen";
  const defaultRowsPerPage = getUserRowsPerPage(user, 10);

  const tablesConfig = useMemo(
    () => [
      {
        key: tableKey,
        label: "Schadenursachen",
        route: "data/schaden-ursachen",
        responseKey: tableKey,
        pageSize: defaultRowsPerPage,
        storageKey,
        extraParams: {
          sort: [{ field: "name", order: "asc" }],
        },
      },
    ],
    [defaultRowsPerPage, storageKey]
  );

  const { tableStates, updateTableState, fetchData } = useMultiTableData(
    tablesConfig,
    enqueueSnackbar
  );

  const tableState = tableStates[tableKey] || {};
  const rows = tableState.rows || [];

  useEffect(() => {
    fetchData(tableKey);
    fetchClaimCauseArten()
      .then(setArten)
      .catch(() =>
        enqueueSnackbar("Schadenarten konnten nicht geladen werden", {
          variant: "error",
        })
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Schadenursache",
        size: 300,
        enableColumnFilter: false,
      },
      {
        id: "art_id",
        accessorFn: (row) => row.art_name ?? "–",
        header: "Schadenart",
        size: 250,
        enableSorting: false,
        filterVariant: "select",
        // MRT's select filter has no "all" entry of its own, so without this
        // option a chosen Schadenart can't be cleared again.
        filterSelectOptions: [
          { value: "", label: "Alle Schadenarten" },
          ...arten.map((art) => ({ value: art.id, label: art.label })),
        ],
      },
    ],
    [arten]
  );

  return (
    <DataTable
      columns={columns}
      data={rows}
      initialState={{ showGlobalFilter: true }}
      isLoading={tableState.isLoading}
      hasLoaded={tableState.hasLoaded}
      pageIndex={tableState.pageIndex}
      pageSize={tableState.pageSize}
      totalRows={tableState.totalRows}
      onChangeSorting={(sorting) => updateTableState(tableKey, { sorting })}
      onChangeFilters={(filters) => updateTableState(tableKey, { filters })}
      onChangePage={(nextOrUpdater) => {
        const base = {
          pageIndex: tableState.pageIndex ?? 0,
          pageSize: tableState.pageSize ?? defaultRowsPerPage,
        };
        const next =
          typeof nextOrUpdater === "function"
            ? nextOrUpdater(base)
            : nextOrUpdater;
        if (!next) {
          return;
        }
        updateTableState(tableKey, {
          pageIndex: next.pageIndex,
          pageSize: next.pageSize,
        });
      }}
      onChangeGlobalFilter={(searchword) =>
        updateTableState(tableKey, { searchword })
      }
      storageKey={storageKey}
      enableHeader
      enableSorting
      enableGlobalFilter={true}
      enableColumnFilters={true}
      enableColumnHiding={true}
    />
  );
}
